import { Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy } from 'react';

import Pipeline from './components/Pipeline/Pipeline.tsx';
import Login from './components/login/Login.tsx';
import ProtectedRoute from './hooks/ProtectedRoute.tsx';
import RoleProtectedRoute from './hooks/RoleProtectedRoute.tsx';
import Home from './components/Home/Home.tsx';
import PipelineDetail from './components/Pipeline/PipelineDetail.tsx';
import Settings from './components/settings/Settings.tsx';
import { NotificationsProvider } from './hooks/NotificationsProvider.tsx';
import { LoaderProvider } from './hooks/LoaderProvider.tsx';
import DashboardPage from './components/Dashboard/DashboardPage.tsx';
import TestAutomation from './components/TestAutomation/TestAutomation.tsx';
import EricssonSwGateway from './components/sw-repositories/EricssonSwGateway.tsx';
import ExternalRepositories from './components/sw-repositories/ExternalRepositories.tsx';
import { UserRoles } from './const/UserManagementTypes';
import NiiLandingPage from './components/NII/NiiLandingPage.tsx';
import PlaceholderReportPage from './components/NII/PlaceholderReportPage.tsx';
import ExecutionTable from './components/NII/ExecutionTable.tsx';
import ScriptManagement from './components/NII/ScriptManagement.tsx';
import AuditReportsLanding from './components/NII/AuditReportsLanding.tsx';
import AuditReleaseNodes from './components/NII/AuditReleaseNodes.tsx';
import NewAuditPage from './components/NII/NewAuditPage.tsx';
import AuditRunDetail from './components/NII/AuditRunDetail.tsx';
import GplNodeReport from './components/NII/GplNodeReport.tsx';
import GnbNodesPage from './components/environments/GnbNodes/GnbNodesPage.tsx';
// Automation Flows
import NewAutomationFlow from './components/automation-flows/NewAutomationFlow.tsx';
import WorkflowConfiguration from './components/automation-flows/configuration/WorkflowConfiguration.tsx';
import AutomationFlowWizardRouter from './components/automation-flows/AutomationFlowWizardRouter.tsx';
// Environments
import EiapClusters from './components/environments/EiapClusters.tsx';
import AddEditEiapCluster from './components/environments/AddEditEiapCluster.tsx';
import Enms from './components/environments/Enms.tsx';
import AddEditEnm from './components/environments/AddEditEnm.tsx';
import EppEnms from './components/environments/EppEnms.tsx';
import CnisEndpoints from './components/environments/CNIS/CnisEndpoint.tsx';
import CnisEndpointForm from './components/environments/CNIS/CnisEndpointForm.tsx';
import MoshellEndpoints from './components/environments/Moshell/Moshell.tsx';
import MoshellEndpointForm from './components/environments/Moshell/MoshellForm.tsx';

import { IS_SHOW_KPI_ANALYTICS, IS_SHOW_TRACEHUNT, SHOW_RSWOB } from './config';
import TestCollection from './components/TestAutomation/TestCollection/TestCollection.tsx';
import TestManagementForm from './components/settings/test_management/testManagementForm.tsx';
import IframeDashboard from './components/IframeDashboard/IframeDashboard.tsx';
import { useAuth } from './hooks/AuthProvider';

// Wrapper to pass user_email to Anomaly Dashboard iframe with optional view path
function AnomalyDashboardEmbed({ viewPath }: { viewPath?: string }) {
  const { user } = useAuth();
  const loginSrc = `/embed/anomaly-dashboard/login`;
  const viewSrc = viewPath ? `/ead/dash/${viewPath}/` : '/ead/dash/';
  const params: Record<string, string> = { user_email: user?.email || user?.username || '' };
  return <IframeDashboard src={loginSrc} title="Anomaly Dashboard" queryParams={params} redirectTo={viewSrc} />;
}

// Wrapper to pass user_email to System Dashboard iframe with optional view path
function SystemDashboardEmbed({ viewPath }: { viewPath?: string }) {
  const { user } = useAuth();
  const params: Record<string, string> = { user_email: user?.email || user?.username || '' };
  const src = viewPath ? `/system-dashboards/${viewPath}` : '/system-dashboards/';
  return <IframeDashboard src={src} title="System Dashboard" queryParams={params} key={viewPath || 'home'} />;
}

const KpiAnalyticsRoutes = IS_SHOW_KPI_ANALYTICS === 'true' ? lazy(() => import('./components/KpiAnalytics/KpiAnalyticsRoutes')) : null;
// Always bundle TraceHunt so the runtime flag can toggle it (visibility gated at route registration)
const TraceHuntRoutes = lazy(() => import('./components/TraceHunt/TraceHuntRoutes'));

function RouteManager() {
  // Silent mode state (lifted globally)
  const [silentMode] = useState(() => {
    const stored = localStorage.getItem('silentMode');
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    localStorage.setItem('silentMode', JSON.stringify(silentMode));
  }, [silentMode]);

  return (
    <LoaderProvider>
      <NotificationsProvider muteNotifications={silentMode}>
        <Routes>
          <Route path="/" element={<Outlet />}>
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute />}>
              <Route index element={<Home />} />
              <Route path="/*" element={<Home />} />
              <Route path="/home" element={<Home />} />

              {SHOW_RSWOB && (
                <>
                  {/* gNB Deploy Nodes */}
                  <Route path="/automation-flows/gnb-nodes" element={<GnbNodesPage />} />
                  {/* Automation Flows */}
                  <Route path="/automation-flows/new" element={<NewAutomationFlow />} />
                  <Route path="/automation-flows/new/wizard" element={<AutomationFlowWizardRouter />} />
                  <Route path="/automation-flows" element={<WorkflowConfiguration />} />
                  <Route path="/automation-flows/workflows" element={<Pipeline />} />
                  <Route path="/automation-flows/:ticketId/:automationFlowId" element={<PipelineDetail />} />
                  <Route path="/automation-flows/test-orchestration/executions" element={<TestAutomation />} />
                  <Route path="/automation-flows/test-orchestration/executions/:status" element={<TestAutomation />} />
                  <Route path="/automation-flows/test-orchestration/management" element={<TestCollection />} />
                  {/* Automation Flows -> Dashboard */}
                  <Route path="/automation-flows/dashboard/overview" element={<DashboardPage />} />
                  <Route path="/automation-flows/dashboard/trends" element={<DashboardPage />} />
                  <Route path="/automation-flows/dashboard/test-automation" element={<DashboardPage />} />
                  {/* Integrations */}
                  <Route path="/integrations/eiap-clusters" element={<EiapClusters />} />
                  <Route path="/integrations/eiap-clusters" element={<EiapClusters />} />
                  <Route path="/integrations/eiap-clusters/new" element={<AddEditEiapCluster />} />
                  <Route path="/integrations/eiap-clusters/edit/:clusterId" element={<AddEditEiapCluster />} />
                  <Route path="/integrations/enms" element={<><Enms /><EppEnms /></>} />
                  <Route path="/integrations/enms/new" element={<AddEditEnm />} />
                  <Route path="/integrations/enms/edit/:enmId" element={<AddEditEnm />} />
                  <Route path="/integrations/cnis" element={<CnisEndpoints />} />
                  <Route path="/integrations/cnis/new" element={<CnisEndpointForm />} />
                  <Route path="/integrations/cnis/edit/:id" element={<CnisEndpointForm />} />
                  <Route path="/integrations/moshell" element={<MoshellEndpoints />} />
                  <Route path="/integrations/moshell/new" element={<MoshellEndpointForm />} />
                  <Route path="/integrations/moshell/edit/:id" element={<MoshellEndpointForm />} />
                  <Route path="/integrations/test-automation" element={<TestManagementForm />} />
                  {/* Integrations -> SW Repositories - Admin Only */}
                  <Route element={<RoleProtectedRoute requiredRole={UserRoles.Admin} />}>
                    <Route path="/integrations/sw-repositories/ericsson-sw-gateway" element={<EricssonSwGateway />} />
                    <Route path="/integrations/sw-repositories/external-repositories" element={<ExternalRepositories />} />
                  </Route>
                  <Route path="/settings" element={<Settings />} />

                  {/* Network Impact Intelligence */}
                  <Route path="/nii" element={<Navigate to="/nii/executions" replace />} />
                  <Route path="/nii/executions" element={<ExecutionTable />} />
                  <Route path="/nii/impact-reports" element={<NiiLandingPage />} />
                  <Route path="/nii/impact-reports/feature/:reportId" element={<PlaceholderReportPage title="Feature Playbook" />} />
                  <Route path="/nii/impact-reports/tr/:reportId" element={<PlaceholderReportPage title="Trouble Report Assessment" />} />
                  <Route path="/nii/audit-reports" element={<AuditReportsLanding />} />
                  <Route path="/nii/audit-reports/new" element={<NewAuditPage />} />
                  <Route path="/nii/audit-reports/release/:gplVersion" element={<AuditReleaseNodes />} />
                  <Route path="/nii/audit-reports/release/:gplVersion/:reportId" element={<GplNodeReport />} />
                  <Route path="/nii/audit-reports/run/:runId" element={<AuditRunDetail />} />
                  <Route path="/nii/audit-reports/run/:runId/:reportId" element={<GplNodeReport />} />
                  <Route path="/nii/script-management" element={<ScriptManagement />} />
                </>
              )}
              {/* KPI Analytics - Dynamically loaded from federated module */}
              <>
                {/* Embedded Dashboards (iframes) */}
                <Route path="/anomaly-dashboard" element={<AnomalyDashboardEmbed />} />
                <Route path="/anomaly-dashboard/anomaly-viewer" element={<AnomalyDashboardEmbed viewPath="anomaly_viewer" />} />
                <Route path="/anomaly-dashboard/interference-viewer" element={<AnomalyDashboardEmbed viewPath="interference_viewer" />} />
                <Route path="/anomaly-dashboard/kpi-viewer" element={<AnomalyDashboardEmbed viewPath="kpi_viewer" />} />
                <Route path="/anomaly-dashboard/moi-comparison" element={<AnomalyDashboardEmbed viewPath="moi_diff_uc" />} />
                <Route path="/anomaly-dashboard/system-constant-viewer" element={<AnomalyDashboardEmbed viewPath="syscon_cli_uc" />} />
                <Route path="/anomaly-dashboard/system-information" element={<AnomalyDashboardEmbed viewPath="system_info_viewer" />} />
                <Route path="/system-dashboard" element={<SystemDashboardEmbed />} />
                {/* Advanced Configuration */}
                <Route path="/advanced-config/administration/users" element={<SystemDashboardEmbed viewPath="administration/users" />} />
                <Route path="/advanced-config/administration/roles" element={<SystemDashboardEmbed viewPath="administration/roles" />} />
                <Route
                  path="/advanced-config/administration/permissions"
                  element={<SystemDashboardEmbed viewPath="administration/permissions" />}
                />
                <Route
                  path="/advanced-config/administration/user-activities"
                  element={<SystemDashboardEmbed viewPath="administration/user-activities" />}
                />
                <Route
                  path="/advanced-config/administration/features"
                  element={<SystemDashboardEmbed viewPath="administration/features" />}
                />
                <Route
                  path="/advanced-config/use-cases/templates"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-templates" />}
                />
                <Route
                  path="/advanced-config/use-cases/instances"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-instances" />}
                />
                <Route
                  path="/advanced-config/use-cases/schedules"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-schedules" />}
                />
                <Route
                  path="/advanced-config/use-cases/subscriptions"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-subscriptions" />}
                />
                <Route
                  path="/advanced-config/use-cases/executions"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-executions" />}
                />
                <Route
                  path="/advanced-config/use-cases/exclusions"
                  element={<SystemDashboardEmbed viewPath="use-cases/use-case-exclusions" />}
                />
                <Route
                  path="/advanced-config/recovery-actions/templates"
                  element={<SystemDashboardEmbed viewPath="recovery-actions/recovery-actions-templates" />}
                />
                <Route
                  path="/advanced-config/recovery-actions/instances"
                  element={<SystemDashboardEmbed viewPath="recovery-actions/recovery-actions-instances" />}
                />
                <Route
                  path="/advanced-config/recovery-actions/executions"
                  element={<SystemDashboardEmbed viewPath="recovery-actions/recovery-actions-executions" />}
                />
                <Route
                  path="/advanced-config/recovery-actions/results"
                  element={<SystemDashboardEmbed viewPath="recovery-actions/recovery-actions-results" />}
                />
                <Route
                  path="/advanced-config/reporters/templates"
                  element={<SystemDashboardEmbed viewPath="reporters/reporter-templates" />}
                />
                <Route
                  path="/advanced-config/reporters/instances"
                  element={<SystemDashboardEmbed viewPath="reporters/reporter-instances" />}
                />
                <Route
                  path="/advanced-config/reporters/executions"
                  element={<SystemDashboardEmbed viewPath="reporters/reporter-executions" />}
                />
                <Route path="/advanced-config/results" element={<SystemDashboardEmbed viewPath="results" />} />
                <Route path="/advanced-config/kpi-definitions" element={<SystemDashboardEmbed viewPath="kpi-definitions" />} />
                <Route path="/advanced-config/global-store" element={<SystemDashboardEmbed viewPath="global-store-instances" />} />
                <Route path="/advanced-config/value/upload" element={<SystemDashboardEmbed viewPath="value/upload" />} />
                <Route
                  path="/advanced-config/value/weekly-achievement"
                  element={<SystemDashboardEmbed viewPath="value/weekly-achievement" />}
                />
                <Route
                  path="/advanced-config/value/nff-recommendation"
                  element={<SystemDashboardEmbed viewPath="value/nff-recommendation" />}
                />
                <Route path="/advanced-config/value/nff-prevented" element={<SystemDashboardEmbed viewPath="value/nff-prevented" />} />
                <Route path="/advanced-config/value/silent-issue" element={<SystemDashboardEmbed viewPath="value/silent-issue" />} />
                <Route
                  path="/advanced-config/value/near-silent-issue"
                  element={<SystemDashboardEmbed viewPath="value/near-silent-issue" />}
                />
                <Route path="/advanced-config/clusters" element={<SystemDashboardEmbed viewPath="clusters" />} />
                <Route path="/advanced-config/epc/nodes" element={<SystemDashboardEmbed viewPath="epc/nodes" />} />
                <Route path="/advanced-config/epc/full-cm" element={<SystemDashboardEmbed viewPath="epc/full-cm/epc" />} />
                <Route path="/advanced-config/feedback" element={<SystemDashboardEmbed viewPath="feedback" />} />
              </>

              {/* KPI Analytics from federated module */}
              {KpiAnalyticsRoutes && <Route path="/use-cases/*" element={<KpiAnalyticsRoutes />} />}
              {KpiAnalyticsRoutes && <Route path="/anomaly-dashboards/*" element={<KpiAnalyticsRoutes />} />}
              {KpiAnalyticsRoutes && <Route path="/dcs-settings/*" element={<KpiAnalyticsRoutes />} />}
              {/* TraceHunt from federated module */}
              {IS_SHOW_TRACEHUNT === 'true' && <Route path="/tracehunt/*" element={<TraceHuntRoutes />} />}
            </Route>
          </Route>
        </Routes>
      </NotificationsProvider>
    </LoaderProvider>
  );
}

export default RouteManager;
